import { Fragment, useCallback, useMemo, useRef } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useImmer } from 'use-immer'
import { useSelector } from 'react-redux'
import shuffle from 'lodash/shuffle'
//
import { selectAdInfo } from 'redux/selector/app'
//
import {
  fetchComicList,
  fetchComicTotalQty,
  fetchNovelList,
  fetchNovelTotalQty,
  type SchemaComicShow,
  type SchemaNovelShow,
  type SchemaShortVideoShow,
  type SchemaVideoShow,
  fetchShortList,
  fetchShortTotalQty,
  fetchVideoList,
  fetchVideoTotalQty,
} from 'api'
//
import { AdvertisementType } from 'constant/advertisement'
//
import HeaderBox from 'component/PageModalHeaderBox'
import VideoBox from 'component/VideoBox'
import VideoBoxVertical from 'component/VideoBoxVertical'
import EmptyResult from 'component/EmptyResult'
import AdItem from './component/AdItem'
//
import ComicBox from 'component/ComicBox'
import {
  AreaPanelCmsGridWrapper,
  HeaderWrap,
  InfiniteScrollWrapper,
  PageScrollWrap,
  PageWrapperNonScroll,
} from 'commonStyledComponents'
import { useParamsOfPage } from 'hooks/useNavigateTo'
import NovelBox from 'component/NovelBox'
import PullToRefresh from 'react-simple-pull-to-refresh'
import PagePullLoad from 'component/Loading/PagePullLoad'

interface ViewMoreState {
  nextPage: number
  isLoading: boolean
  hasMore: boolean
  list: ViewMoreItem[]
  page_result: {
    current: number | null
    pageSize: number | null
    total: number | null
  }
  loadedPages: number[]
}

type ViewMoreItem = SchemaVideoShow | SchemaShortVideoShow | SchemaComicShow | SchemaNovelShow

interface FetchVideoListOptions {
  reset?: boolean
}

export default function ViewMore() {
  const { type = 'video', params, headerTitle: headerTitleProp, showAd = true } = useParamsOfPage('view-more')

  const adInfo = useSelector(selectAdInfo)
  const adViewMore = useMemo(() => {
    return adInfo?.[AdvertisementType.ViewMore]
  }, [adInfo])
  // 是否需要亂數排序
  const randomSort = useMemo(() => {
    return params.random
  }, [params])

  const [state, updateState] = useImmer<ViewMoreState>({
    nextPage: 1,
    isLoading: true,
    hasMore: true,
    list: [],
    page_result: { current: null, pageSize: null, total: null },
    loadedPages: [],
  })
  const loading = useRef(false)
  const activeRequest = useRef<AbortController | null>(null)

  const headerTitle = headerTitleProp || '看更多'

  const itemPerRow =
    type === 'video'
      ? params?.isVerticalVideoBox
        ? 2
        : params?.isLargeVideoBox
        ? 1
        : 2
      : type === 'short'
      ? 2
      : type === 'comic'
      ? 3
      : type === 'novel'
      ? 3
      : 2 // as default

  const fetchVideoApi = useMemo(() => {
    if (type === 'short') return fetchShortList
    if (type === 'comic') return fetchComicList
    if (type === 'novel') return fetchNovelList
    return fetchVideoList
  }, [type])

  const fetchTotalApi = useMemo(() => {
    if (type === 'short') return fetchShortTotalQty
    else if (type === 'comic') return fetchComicTotalQty
    else if (type === 'novel') return fetchNovelTotalQty
    else return fetchVideoTotalQty
  }, [type])

  const handleFetchVideoList = useCallback(
    async ({ reset = false }: FetchVideoListOptions = {}) => {
      if (!reset && (!state.hasMore || loading.current)) return

      const requestState = reset
        ? {
            nextPage: 1,
            page_result: { current: null, pageSize: null, total: null },
            loadedPages: [] as number[],
          }
        : state

      if (reset) {
        activeRequest.current?.abort()
      }

      const controller = new AbortController()
      activeRequest.current = controller
      loading.current = true

      if (reset) {
        updateState((draft) => {
          draft.list = []
          draft.hasMore = true
          draft.nextPage = 1
          draft.page_result = { current: null, pageSize: null, total: null }
          draft.isLoading = true
          draft.loadedPages = []
        })
      }

      const requestParams: any = {
        ...params,
        ...(params.pageSize === undefined ? { pageSize: 30 } : {}),
        signal: controller.signal,
      }
      // 清除不必要的參數，避免影響後端排序
      delete requestParams.isVerticalVideoBox
      delete requestParams.isLargeVideoBox
      delete requestParams.random

      try {
        let next = requestState.nextPage
        let hasMore = false

        if (randomSort) {
          const totalResponse = requestState.page_result.total ? undefined : await fetchTotalApi(requestParams)
          if (controller.signal.aborted) return

          const total = totalResponse?.page_result?.total ?? requestState.page_result.total ?? 0

          if (totalResponse?.page_result) {
            updateState((draft) => {
              draft.page_result = totalResponse.page_result as ViewMoreState['page_result']
            })
          }

          if (!total) {
            updateState((draft) => {
              draft.list = []
              draft.hasMore = false
              draft.nextPage = 1
              draft.isLoading = false
              draft.loadedPages = []
            })
            return
          }

          const pages = Math.ceil(total / requestParams.pageSize)
          const pageNumbers = Array.from({ length: pages }, (_, index) => index + 1)
          const unloadedPages = pageNumbers.filter((page) => !requestState.loadedPages.includes(page))
          if (unloadedPages.length === 0) {
            updateState((draft) => {
              draft.hasMore = false
              draft.isLoading = false
            })
            return
          }

          next = unloadedPages[Math.floor(Math.random() * unloadedPages.length)]
          hasMore = unloadedPages.length > 1
        }

        const response = await fetchVideoApi({ ...requestParams, current: next })
        if (controller.signal.aborted) return

        let items: ViewMoreItem[] = response?.data ?? []
        if (randomSort) {
          items = shuffle(items)
        } else {
          hasMore = response?.page_result?.total > response?.page_result?.current * response?.page_result?.pageSize
        }

        updateState((draft) => {
          if (draft.loadedPages.includes(next)) return
          draft.list = reset ? items : draft.list.concat(items)
          draft.hasMore = hasMore
          draft.nextPage = response?.page_result?.current + 1
          draft.page_result = response.page_result as ViewMoreState['page_result']
          draft.isLoading = false
          draft.loadedPages = reset ? [next] : [...draft.loadedPages, next]
        })
      } catch (error) {
        if (!controller.signal.aborted) throw error
      } finally {
        if (activeRequest.current === controller) {
          activeRequest.current = null
          loading.current = false
        }
      }
    },
    [fetchTotalApi, fetchVideoApi, params, randomSort, state, updateState]
  )

  const handleLoadMore = useCallback(() => handleFetchVideoList(), [handleFetchVideoList])

  const handleRefresh = useCallback(() => handleFetchVideoList({ reset: true }), [handleFetchVideoList])

  const shortInfo = useMemo(() => {
    return {
      list: state.list as object[],
    }
  }, [state.list])

  return (
    <PageWrapperNonScroll>
      <HeaderWrap>
        <HeaderBox headerTitle={headerTitle} />
      </HeaderWrap>
      {!state?.list?.length && !state.isLoading ? (
        <EmptyResult />
      ) : (
        <PageScrollWrap className="lazy-root">
          <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent={<PagePullLoad paused />}
            refreshingContent={<PagePullLoad />}
          >
            <InfiniteScrollWrapper
              pageStart={0}
              loadMore={handleLoadMore}
              hasMore={state?.hasMore}
              // loader={<div className="loader" key={0}>Loading ...</div>}
              useWindow={false}
            >
              {state?.list?.length > 0 && (
                <AreaPanelCmsGridWrapper>
                  {state?.list?.map((item, i) => {
                    /** 每四行 放置一個 視頻櫥窗更多廣告位 */
                    const itemsPerAd = itemPerRow * 4
                    const isPutAdItem = showAd ? (i + 1) % itemsPerAd === 0 : false
                    const adIndex = ((i + 1) / itemsPerAd) % adViewMore?.length
                    const adItemInfo = adViewMore?.[adIndex]

                    return (
                      <Fragment key={i}>
                        {type === 'video' &&
                          (params?.isVerticalVideoBox ? (
                            <VideoBoxVertical columns={itemPerRow} videoInfo={item} />
                          ) : (
                            <VideoBox columns={itemPerRow} videoInfo={item} />
                          ))}
                        {type === 'short' && (
                          <VideoBoxVertical columns={itemPerRow} videoInfo={item} shortInfo={shortInfo} />
                        )}
                        {type === 'comic' && <ComicBox columns={itemPerRow} comicInfo={item as SchemaComicShow} />}
                        {type === 'novel' && <NovelBox columns={itemPerRow} novelInfo={item as SchemaNovelShow} />}
                        {isPutAdItem && !isEmpty(adItemInfo) && <AdItem {...adItemInfo} />}
                      </Fragment>
                    )
                  })}
                </AreaPanelCmsGridWrapper>
              )}
            </InfiniteScrollWrapper>
          </PullToRefresh>
        </PageScrollWrap>
      )}
    </PageWrapperNonScroll>
  )
}
